import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Eye, EyeOff } from 'lucide-react';

interface AccountDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (_currentPassword: string) => void;
  isLoading?: boolean;
  errorMessage?: string;
  requirePassword?: boolean;
}

const AccountDeletionModal: React.FC<AccountDeletionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  errorMessage = '',
  requirePassword = true,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const confirmKeyword = 'DELETE';

  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setCurrentPassword('');
      setShowPassword(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isKeywordValid = confirmText === confirmKeyword;
  const isPasswordValid = !requirePassword || currentPassword.trim().length > 0;
  const isConfirmValid = isKeywordValid && isPasswordValid;

  const handleConfirm = () => {
    if (!isConfirmValid || isLoading) return;
    onConfirm(currentPassword);
  };

  return (
    <div className="fixed inset-0 bg-[var(--modal-overlay-background)] bg-opacity-50 flex items-center justify-center z-[100] p-4 overflow-y-auto zoomOutLevel">
      <div className="bg-[var(--modal-background)] rounded-[20px] md:p-8 p-6 w-full max-w-lg mx-auto my-auto shadow-xl animate-scale-in max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-[var(--color-text)]">Delete Account</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto scrollbar mb-4 space-y-4 min-h-0">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm font-medium mb-2">
              Warning: This action cannot be undone
            </p>
            <p className="text-red-700 text-sm">
              Deleting your account will permanently remove all your data, including:
            </p>
            <ul className="list-disc list-inside text-red-700 text-sm mt-2 space-y-1 ml-2">
              <li>Your profile and personal information</li>
              <li>All your devices and their data</li>
              <li>Your preferences and settings</li>
              <li>All historical data and reports</li>
            </ul>
          </div>

          {/* Confirmation keyword */}
          <div className="flex-shrink-0">
            <p className="text-[var(--color-sub-title-text)] text-base font-normal mb-3 break-words">
              To confirm account deletion, please type <strong className="text-red-600">DELETE</strong> in the box below:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-red-500 text-base ${
                confirmText && confirmText !== confirmKeyword
                  ? 'border-red-500 focus:border-red-500'
                  : confirmText === confirmKeyword
                  ? 'border-green-500 focus:border-green-500'
                  : 'border-red-300 focus:border-red-500'
              }`}
              disabled={isLoading}
              autoFocus
              style={{ boxSizing: 'border-box' }}
            />
          </div>

          {/* Current password (security step-up) */}
          {requirePassword && (
            <div className="flex-shrink-0">
              <p className="text-[var(--color-sub-title-text)] text-base font-normal mb-3 break-words">
                For your security, please enter your <strong>current password</strong> to continue:
              </p>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  className="w-full px-4 py-3 pr-12 border-2 rounded-lg focus:outline-none focus:ring-red-500 text-base border-red-300 focus:border-red-500"
                  disabled={isLoading}
                  autoComplete="current-password"
                  style={{ boxSizing: 'border-box' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          )}

          {/* Error message (e.g. wrong password from server) */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{errorMessage}</p>
            </div>
          )}
        </div>

        {/* Actions - Fixed at bottom */}
        <div className="flex flex-row gap-3 justify-end flex-shrink-0 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="secondary-button"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="primary-button"
            style={{ background: 'linear-gradient(to right, #dc2626, #b91c1c)' }}
            disabled={isLoading || !isConfirmValid}
          >
            {isLoading ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Deleting Account...</span>
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountDeletionModal;
